import { useState, useEffect, useCallback } from 'react';
import PageHeader from '../../components/PageHeader';
import LoadingSpinner from '../../components/LoadingSpinner';
import { apiRequest } from '../../services/api';

const VARSAYILAN_SAYFALAR = [
  { sayfaKodu: 'odalar',      menuBaslik: 'Odalar',      aktifMi: true,  sira: 1 },
  { sayfaKodu: 'rezervasyon', menuBaslik: 'Rezervasyon', aktifMi: true,  sira: 2 },
  { sayfaKodu: 'hakkimizda',  menuBaslik: 'Hakkımızda',  aktifMi: true,  sira: 3 },
  { sayfaKodu: 'galeri',      menuBaslik: 'Galeri',      aktifMi: false, sira: 4 },
  { sayfaKodu: 'iletisim',    menuBaslik: 'İletişim',    aktifMi: true,  sira: 5 },
  { sayfaKodu: 'referanslar', menuBaslik: 'Referanslar', aktifMi: false, sira: 6 },
];

const SAYFA_ACIKLAMALARI = {
  odalar:      'Odaları ve oda tiplerini gösterir.',
  rezervasyon: 'Misafirlerin rezervasyon yapabileceği formu içerir.',
  hakkimizda:  'Otel hakkında bilgi ve görsel içerir.',
  galeri:      'Fotoğraf galerisini gösterir.',
  iletisim:    'Adres, telefon, e-posta ve harita bilgilerini içerir.',
  referanslar: 'Misafir yorumları ve referansları gösterir.',
};

export default function WebSiteSayfalar() {
  const [yukleniyor, setYukleniyor] = useState(true);
  const [sayfalar, setSayfalar] = useState(VARSAYILAN_SAYFALAR);
  const [kaydediliyor, setKaydediliyor] = useState(false);
  const [mesaj, setMesaj] = useState('');

  const yukle = useCallback(async () => {
    setYukleniyor(true);
    try {
      const data = await apiRequest('website');
      if (data?.sayfalar?.length > 0) {
        const mevcutKodlar = data.sayfalar.map(s => s.sayfaKodu);
        const eksikler = VARSAYILAN_SAYFALAR.filter(s => !mevcutKodlar.includes(s.sayfaKodu));
        const birlestir = [...data.sayfalar, ...eksikler].sort((a, b) => a.sira - b.sira);
        setSayfalar(birlestir.map((s, idx) => ({
          sayfaKodu: s.sayfaKodu,
          menuBaslik: s.menuBaslik || '',
          aktifMi: s.aktifMi ?? true,
          sira: idx + 1,
        })));
      }
    } catch { }
    setYukleniyor(false);
  }, []);

  useEffect(() => { yukle(); }, [yukle]);

  function toggle(idx) {
    setSayfalar(p => p.map((s, i) => i === idx ? { ...s, aktifMi: !s.aktifMi } : s));
  }

  function baslikDegistir(idx, v) {
    setSayfalar(p => p.map((s, i) => i === idx ? { ...s, menuBaslik: v } : s));
  }

  function yukariTasi(idx) {
    if (idx === 0) return;
    setSayfalar(p => {
      const yeni = [...p];
      [yeni[idx - 1], yeni[idx]] = [yeni[idx], yeni[idx - 1]];
      return yeni.map((s, i) => ({ ...s, sira: i + 1 }));
    });
  }

  function asagiTasi(idx) {
    setSayfalar(p => {
      if (idx === p.length - 1) return p;
      const yeni = [...p];
      [yeni[idx], yeni[idx + 1]] = [yeni[idx + 1], yeni[idx]];
      return yeni.map((s, i) => ({ ...s, sira: i + 1 }));
    });
  }

  async function kaydet() {
    setKaydediliyor(true);
    try {
      await apiRequest('website/sayfalar', 'PUT', sayfalar);
      setMesaj('✅ Sayfa yapısı kaydedildi.');
    } catch (e) {
      setMesaj('❌ ' + (e.message || 'Kaydedilemedi.'));
    } finally {
      setKaydediliyor(false);
      setTimeout(() => setMesaj(''), 3000);
    }
  }

  if (yukleniyor) return <LoadingSpinner fullPage text="Sayfa yapısı yükleniyor..." />;

  return (
    <div className="p-4 md:p-6 space-y-6 min-h-screen bg-gray-50">
      <PageHeader icon="📄" iconColor="from-indigo-600 to-purple-700" iconShadow="shadow-indigo-200"
        title="Web Sitesi — Sayfalar"
        subtitle="Hangi sayfaların gösterileceğini ve menü sıralamasını yönetin"
      >
        <button onClick={kaydet} disabled={kaydediliyor}
          className="bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white px-6 py-2 rounded-xl text-sm font-bold transition-all">
          {kaydediliyor ? 'Kaydediliyor...' : 'Kaydet'}
        </button>
      </PageHeader>

      {mesaj && (
        <div className={`p-4 rounded-xl text-sm font-semibold ${mesaj.startsWith('✅') ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
          {mesaj}
        </div>
      )}

      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100 bg-gray-50">
          <p className="text-xs font-bold text-gray-500 uppercase tracking-wide">Navigasyon Menüsü Yapısı</p>
        </div>
        <div className="divide-y divide-gray-50">
          {sayfalar.map((sayfa, idx) => (
            <div key={sayfa.sayfaKodu}
              className={`flex items-center gap-4 px-6 py-4 transition-colors ${sayfa.aktifMi ? 'bg-white' : 'bg-gray-50'}`}>
              <div className="flex flex-col gap-0.5">
                <button onClick={() => yukariTasi(idx)} disabled={idx === 0}
                  className="w-6 h-6 text-gray-400 hover:text-gray-700 disabled:opacity-20 text-xs">▲</button>
                <span className="text-xs font-bold text-gray-300 text-center leading-none">{idx + 1}</span>
                <button onClick={() => asagiTasi(idx)} disabled={idx === sayfalar.length - 1}
                  className="w-6 h-6 text-gray-400 hover:text-gray-700 disabled:opacity-20 text-xs">▼</button>
              </div>

              <div onClick={() => toggle(idx)}
                className={`relative w-10 h-6 rounded-full transition-colors cursor-pointer flex-shrink-0 ${sayfa.aktifMi ? 'bg-green-500' : 'bg-gray-200'}`}>
                <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${sayfa.aktifMi ? 'translate-x-4' : 'translate-x-0'}`} />
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-0.5">
                  <span className="text-xs font-mono text-gray-400">/{sayfa.sayfaKodu}</span>
                  {!sayfa.aktifMi && <span className="text-[10px] bg-gray-100 text-gray-400 px-2 py-0.5 rounded-full font-bold">GİZLİ</span>}
                </div>
                <p className="text-xs text-gray-400">{SAYFA_ACIKLAMALARI[sayfa.sayfaKodu] || ''}</p>
              </div>

              <div className="w-44 flex-shrink-0">
                <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wide block mb-1">Menü Başlığı</label>
                <input
                  className="w-full border border-gray-200 rounded-lg px-3 py-1.5 text-sm font-semibold text-gray-700 focus:outline-none focus:border-indigo-400 bg-white"
                  value={sayfa.menuBaslik}
                  onChange={e => baslikDegistir(idx, e.target.value)}
                  placeholder={sayfa.sayfaKodu}
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-sm text-blue-700">
        <strong>Not:</strong> Ana Sayfa varsayılan olarak her zaman gösterilir. Menüde görünmesini istemediğiniz
        sayfaları toggle ile kapatın. Sıralama, menüdeki görünüm sırasını belirler.
      </div>
    </div>
  );
}
